/**
 * Device utilities for generating and managing device identifiers
 */

/**
 * Generate a unique device ID for the current browser/device
 * Uses a combination of browser fingerprinting and localStorage
 */
export function generateDeviceId(): string {
  // Try to get existing device ID from localStorage
  const existingDeviceId = localStorage.getItem('device_id')
  if (existingDeviceId) {
    return existingDeviceId
  }

  // Generate a new device ID using browser characteristics
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillText('Device fingerprint', 2, 2)
  }

  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    canvas.toDataURL(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.maxTouchPoints || 'unknown'
  ].join('|')

  // Create a simple hash from the fingerprint
  let hash = 0
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }

  // Generate device ID with timestamp for uniqueness
  const timestamp = Date.now().toString(36)
  const randomSuffix = Math.random().toString(36).substring(2, 8)
  const deviceId = `web-${Math.abs(hash).toString(36)}-${timestamp}-${randomSuffix}`

  // Store in localStorage for future use
  localStorage.setItem('device_id', deviceId)
  
  return deviceId
}

/**
 * Get the current device ID (generate if not exists)
 */
export function getDeviceId(): string {
  return generateDeviceId()
}

/**
 * Get device type based on user agent
 */
export function getDeviceType(): 'web' | 'mobile' | 'tablet' {
  const userAgent = navigator.userAgent.toLowerCase()
  
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
    return 'mobile'
  } else if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return 'tablet'
  }
  
  return 'web'
}

/**
 * Get device information object
 */
export function getDeviceInfo() {
  return {
    deviceId: getDeviceId(),
    deviceType: getDeviceType(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString()
  }
}
